import React, { useState } from "react";
import {
    Modal, Upload, Button, Space, Table, Card, Alert, message, Tag,
    Row, Col, Statistic, Typography, Radio, Checkbox, Divider,
} from "antd";
import { InboxOutlined, CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { settlementApi } from "../../../../api/settlementApi";

const { Text } = Typography;
const { Dragger } = Upload;

const FlightRecordImportModal = ({ open, onClose, onSuccess }) => {
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [duplicateStrategy, setDuplicateStrategy] = useState("skip_all");
    const [selectedOverrideIds, setSelectedOverrideIds] = useState([]);

    const resetState = () => {
        setFileList([]);
        setImportResult(null);
        setLoading(false);
        setDuplicateStrategy("skip_all");
        setSelectedOverrideIds([]);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const doImport = async (strategy, overrideIds) => {
        setLoading(true);
        try {
            const file = fileList[0].originFileObj || fileList[0];
            const res = await settlementApi.flightRecord.importRecords(file, strategy, overrideIds);
            const result = res.data || res;
            setImportResult(result);

            if (result.valid_rows > 0) {
                message.success(`成功导入 ${result.valid_rows} 条记录`);
            }
            if (result.error_rows > 0) {
                message.warning(`${result.error_rows} 条记录解析异常`);
            }
            if (result.warnings?.length > 0) {
                message.info(`${result.warnings.length} 条数据质量提醒，请查看详情`);
            }
        } catch (err) {
            message.error(`导入失败: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleFirstImport = () => {
        if (!fileList.length) {
            message.warning("请先选择文件");
            return;
        }
        doImport("skip_all", null);
    };

    const handleRetryWithStrategy = () => {
        const overrideIds = duplicateStrategy === "selective" ? selectedOverrideIds : null;
        doImport(duplicateStrategy, overrideIds);
    };

    const handleDone = () => {
        onSuccess();
        resetState();
    };

    const errorColumns = [
        { title: "行号", dataIndex: "row", width: 70 },
        { title: "原因", dataIndex: "reason", ellipsis: true },
    ];

    const duplicateColumns = [
        {
            title: "",
            width: 40,
            render: (_, rec) => (
                duplicateStrategy === "selective" ? (
                    <Checkbox
                        checked={selectedOverrideIds.includes(rec.existing_id)}
                        onChange={(e) => {
                            setSelectedOverrideIds((prev) =>
                                e.target.checked
                                    ? [...prev, rec.existing_id]
                                    : prev.filter((id) => id !== rec.existing_id)
                            );
                        }}
                    />
                ) : null
            ),
        },
        { title: "航班号", dataIndex: "flight_number_norm", width: 100 },
        { title: "日期", dataIndex: "flight_date", width: 110 },
        { title: "航段", dataIndex: "segment", width: 100 },
        {
            title: "已有BH",
            dataIndex: "existing_bh",
            width: 80,
            render: (v) => v != null ? v.toFixed(2) : "-",
        },
        {
            title: "新BH",
            dataIndex: "new_bh",
            width: 80,
            render: (v) => v != null ? Number(v).toFixed(2) : "-",
        },
    ];

    const hasResult = importResult != null;
    const hasDuplicates = importResult?.duplicates?.length > 0;
    const hasErrors = importResult?.errors?.length > 0;
    const hasWarnings = importResult?.warnings?.length > 0;

    const renderFooter = () => {
        if (!hasResult) {
            return (
                <Space>
                    <Button onClick={handleClose}>取消</Button>
                    <Button type="primary" onClick={handleFirstImport} loading={loading} disabled={!fileList.length}>
                        解析并导入
                    </Button>
                </Space>
            );
        }
        return (
            <Space>
                {hasDuplicates && (
                    <Button
                        onClick={handleRetryWithStrategy}
                        loading={loading}
                        disabled={duplicateStrategy === "selective" && selectedOverrideIds.length === 0}
                    >
                        按策略处理重复
                    </Button>
                )}
                <Button type="primary" onClick={handleDone}>完成</Button>
            </Space>
        );
    };

    return (
        <Modal
            title="导入飞行记录表"
            open={open}
            onCancel={handleClose}
            width={720}
            footer={renderFooter()}
            destroyOnHidden
        >
            {!hasResult ? (
                <Dragger
                    accept=".xlsx,.xls"
                    fileList={fileList}
                    beforeUpload={() => false}
                    onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
                    maxCount={1}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">点击或拖拽飞行记录表 Excel 到此处</p>
                    <p className="ant-upload-hint">支持 .xlsx / .xls 格式，23 列飞行记录表</p>
                </Dragger>
            ) : (
                <div>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col span={5}>
                            <Card size="small">
                                <Statistic title="总行数" value={importResult.total_rows} />
                            </Card>
                        </Col>
                        <Col span={5}>
                            <Card size="small">
                                <Statistic title="有效写入" value={importResult.valid_rows} valueStyle={{ color: "#52c41a" }} />
                            </Card>
                        </Col>
                        <Col span={5}>
                            <Card size="small">
                                <Statistic title="异常行" value={importResult.error_rows} valueStyle={importResult.error_rows > 0 ? { color: "#ff4d4f" } : {}} />
                            </Card>
                        </Col>
                        <Col span={5}>
                            <Card size="small">
                                <Statistic title="数据提醒" value={importResult.warnings?.length || 0} valueStyle={hasWarnings ? { color: "#faad14" } : {}} />
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card size="small">
                                <Statistic title="耗时" value={`${importResult.elapsed_ms}ms`} />
                            </Card>
                        </Col>
                    </Row>

                    {importResult.match_summary && !importResult.match_summary.error && (
                        <Alert
                            type="info"
                            message="自动匹配结果"
                            description={
                                <Space>
                                    <Tag color="success">已匹配 {importResult.match_summary.matched}</Tag>
                                    <Tag color="warning">多候选 {importResult.match_summary.ambiguous}</Tag>
                                    <Tag color="error">未匹配 {importResult.match_summary.unmatched}</Tag>
                                </Space>
                            }
                            style={{ marginBottom: 16 }}
                            icon={<CheckCircleOutlined />}
                            showIcon
                        />
                    )}

                    {importResult.match_summary?.suspicious?.length > 0 && (
                        <Alert
                            type="error"
                            message={`${importResult.match_summary.suspicious.length} 条可疑匹配（Excel Off 与 ATD 时间差距过大，可能匹配错误）`}
                            description={
                                <ul style={{ margin: "8px 0 0", paddingLeft: 20, maxHeight: 160, overflowY: "auto", fontSize: 13 }}>
                                    {importResult.match_summary.suspicious.map((s, i) => (
                                        <li key={i} style={{ marginBottom: 2 }}>
                                            <Text strong>{s.flight_number}</Text> {s.flight_date}
                                            {" — "}Excel Off <Text code>{s.excel_off}</Text> vs ATD <Text code>{s.fop_atd}</Text>
                                            ，差距 <Text type="danger">{s.diff_hours}h</Text>
                                        </li>
                                    ))}
                                </ul>
                            }
                            icon={<WarningOutlined />}
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {hasWarnings && (
                        <Alert
                            type="warning"
                            message={`${importResult.warnings.length} 条数据质量提醒（已正常导入，建议核实）`}
                            description={
                                <ul style={{ margin: "8px 0 0", paddingLeft: 20, maxHeight: 160, overflowY: "auto", fontSize: 13 }}>
                                    {importResult.warnings.map((w, i) => (
                                        <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                                    ))}
                                </ul>
                            }
                            icon={<WarningOutlined />}
                            showIcon
                            closable
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {hasDuplicates && (
                        <div style={{ marginBottom: 16 }}>
                            <Alert
                                type="warning"
                                message={`检测到 ${importResult.duplicates.length} 条重复记录（已跳过），可选择策略重新处理`}
                                icon={<WarningOutlined />}
                                showIcon
                                style={{ marginBottom: 8 }}
                            />
                            <div style={{ marginBottom: 8 }}>
                                <Text strong>重复处理策略：</Text>
                                <Radio.Group
                                    value={duplicateStrategy}
                                    onChange={(e) => { setDuplicateStrategy(e.target.value); setSelectedOverrideIds([]); }}
                                    style={{ marginLeft: 12 }}
                                >
                                    <Radio value="override_all">全部覆盖</Radio>
                                    <Radio value="selective">逐条选择</Radio>
                                    <Radio value="skip_all">全部跳过</Radio>
                                </Radio.Group>
                            </div>
                            <Table
                                size="small"
                                columns={duplicateColumns}
                                dataSource={importResult.duplicates}
                                rowKey="existing_id"
                                pagination={false}
                                scroll={{ y: 200 }}
                            />
                        </div>
                    )}

                    {hasErrors && (
                        <div>
                            <Text type="danger" strong>异常行详情：</Text>
                            <Table
                                size="small"
                                columns={errorColumns}
                                dataSource={importResult.errors}
                                rowKey={(r) => `${r.row}-${r.reason}`}
                                pagination={false}
                                scroll={{ y: 200 }}
                                style={{ marginTop: 8 }}
                            />
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default FlightRecordImportModal;
